import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from "motion/react";
import { HamburgerMenu } from '@/components';
import { ChevronRightIcon } from '@/components/icons';
import { ClubFooter } from '@/features/clubs/components';
import { useEventBookingCategories } from '@/features/booking/hooks/useEventBookingCategories';
import { CategoryItem } from '@/features/booking/components/CategoryItem';
import { useLoadingContext, useAuth } from '@/hooks';
import { getSignedMediaUrl } from '@/lib/utils';

import type { ProcessedBookingCategory } from '@/features/booking/types/eventBookingCategoryTypes';
import type { Business } from '@/features/clubs/types/types';

/**
 * Event Schedule Category Page - displays booking categories for tickets or tables
 */
const EventScheduleCategoryPage: React.FC = () => {
  const { eventScheduleId, handle, eventId } = useParams<{ eventScheduleId: string; handle: string; eventId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoadingContext();
  const { isAuthenticated } = useAuth();
  
  // Get booking type from URL params (default to 'tickets')
  const bookingType = (searchParams.get('type') as 'tickets' | 'tables') || 'tickets';
  
  // Fetch booking categories
  const { bookingCategories, loading, error } = useEventBookingCategories(
    eventScheduleId || '', 
    bookingType
  );

  // Trigger loading when data is loading
  React.useEffect(() => {
    if (loading) {
      startLoading('booking-categories-data');
    } else {
      stopLoading('booking-categories-data');
    }
    
    // Cleanup on unmount
    return () => {
      stopLoading('booking-categories-data');
    };
  }, [loading, startLoading, stopLoading]);

  // Get event info from first category (they all have the same event)
  const eventInfo = bookingCategories[0]?.event;
  const businessInfo = bookingCategories[0]?.business;
  
  // Use business info directly for ClubFooter (type assertion needed due to minor type differences)
  const clubData = businessInfo as Business | null;
  
  // Use event card_image for hero background
  const heroBackgroundImage = eventInfo?.card_image_id && eventInfo.card_image_id !== '00000000-0000-0000-0000-000000000000' 
    ? getSignedMediaUrl(eventInfo.card_image_id, 'image').url 
    : '';

  const handleBook = (category: ProcessedBookingCategory) => {
    // Navigate to booking flow - you can customize this based on your routing structure
    if (category.is_table_booking) {
      navigate(`/booking/reservations/${category.id}`);
    } else {
      navigate(`/booking/tickets/${category.id}`);
    }
  };

  const handleBack = () => {
    // Prefer using URL params for reliable navigation
    if (handle && eventId) {
      navigate(`/venues/${handle}/events/${eventId}`);
    } else if (eventInfo?.id && businessInfo?.handle) {
      navigate(`/venues/${businessInfo.handle}/events/${eventInfo.id}`);
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className="bg-black min-h-screen">
        {/* Loading is handled by the global loader */}
      </div>
    );
  }

  if (error || !eventScheduleId) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Booking Options Not Found</h1>
          <p className="text-gray-400 mb-6">
            {error || 'The booking options you are looking for do not exist.'}
          </p>
          <button 
            onClick={handleBack}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (bookingCategories.length === 0) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">No {bookingType} Available</h1>
          <p className="text-gray-400 mb-6">
            There are currently no {bookingType} available for this event.
          </p>
          <button 
            onClick={handleBack}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Extract club handle for hamburger menu and context
  // Prioritize handle from URL, then business info
  const effectiveClubHandle = handle || businessInfo?.handle || null;

  return (
    <>
      <div className="bg-black text-white min-h-screen overflow-x-hidden">
        <HamburgerMenu clubHandle={effectiveClubHandle} isAuthenticated={isAuthenticated} />
        {/* Background Image */}
        {heroBackgroundImage && (
          <div className="absolute top-0 left-0 h-[70vh] w-full -z-10">
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-20" 
              style={{ backgroundImage: `url('${heroBackgroundImage}')`, filter: 'blur(8px)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/80 to-black" />
          </div>
        )}

        {/* Breadcrumb Navigation */}
        <div className="relative z-10 pt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <nav className="text-sm text-gray-400 flex items-center">
              <button 
                onClick={() => navigate(`/venues/${effectiveClubHandle}`)}
                className="hover:text-white transition-colors cursor-pointer"
              >
                {effectiveClubHandle || 'Venue'}
              </button>
              <ChevronRightIcon className="mx-2 w-4 h-4" />
              <button 
                onClick={handleBack}
                className="hover:text-white transition-colors cursor-pointer"
              >
                {eventInfo?.name || 'Event'}
              </button>
              <ChevronRightIcon className="mx-2 w-4 h-4" />
              <span className="text-white capitalize">{bookingType}</span>
            </nav>
          </div>
        </div>

        <main className="relative max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-24 z-10">
          {/* Header */}
          <motion.div 
            className="text-center pt-16 pb-20" 
            initial={{ opacity: 0, y: -30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h1 className="text-5xl md:text-7xl font-serif font-bold tracking-tighter mb-4">
              {eventInfo?.name || 'Event Bookings'}
            </h1>
            <p className="text-lg md:text-xl text-gray-400 capitalize">
              Choose Your {bookingType === 'tickets' ? 'Ticket' : 'Table'} Experience
            </p>
          </motion.div>

          {/* Booking Categories */}
          <div>
            {bookingCategories.map((category, index) => (
              <CategoryItem
                key={category.id}
                category={category}
                index={index}
                onBook={handleBook}
              />
            ))}
          </div>
        </main>
        
        {/* Club Footer */}
        {clubData && <ClubFooter clubData={clubData} />}
      </div>
    </>
  );
};

export default EventScheduleCategoryPage;
